import React from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { accessorialCanBeInvoiced, accessorialPaid, userCanApproveAccessorials as userCanApproveAccessorialsHelper, checkIfEquipmentIsMissingForAccessorial, checkWhichSideAccessorialAutoDeclined, isCarrierMissingInAccessorial, } from 'helpers/accessorials';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { isShipmentCanceled } from 'helpers/shipments';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import EditButton from 'components/v2/buttons/EditButton';
import SessionStore from 'stores/SessionStore';
import USER_TYPES from 'constants/UserTypes';
import { TOOLTIPS } from './accessorialTooltips';
function getToolTip(buttonType, isMissingEquipment = false, declinedAccessorial = false, isCarrierMissing = false) {
    if (declinedAccessorial) {
        return TOOLTIPS.SHIPPER_CHANGED;
    }
    if (isCarrierMissing) {
        return TOOLTIPS.NO_CARRIER_ASSIGNED_INFO;
    }
    if (isMissingEquipment && buttonType === 'approve') {
        return TOOLTIPS.MISSING_EQUIPMENT;
    }
    return TOOLTIPS.EMPTY_TOOLTIP;
}
function userCanApproveAccessorial(accessorial) {
    const user = SessionStore.getUser();
    return userCanApproveAccessorialsHelper(user, [accessorial]);
}
function checkMissingDocsByUserType(accessorial, type) {
    if (type === USER_TYPES.CARRIER) {
        return !!(accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs);
    }
    return (!!(accessorial === null || accessorial === void 0 ? void 0 : accessorial.missing_docs) ||
        (accessorial === null || accessorial === void 0 ? void 0 : accessorial.customer_approval_provided) === false);
}
const AccessorialActionButtons = ({ accessorial, accessorialId, shipment, type, isAccessorialsAdmin, accessorialFetched, isEditSaving, isApproveSaving, isDeclineSaving, isEditable, onEdit, onApprove, onDecline, }) => {
    var _a, _b;
    const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
    const isVendorPaid = (accessorial === null || accessorial === void 0 ? void 0 : accessorial.carrier_status) === 'approved' &&
        isVendorChargePaid(accessorialId, shipment === null || shipment === void 0 ? void 0 : shipment.vendor_payables) &&
        type === USER_TYPES.CARRIER;
    const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
    const carrierAssociatedWithAccessorial = ((_b = accessorial.carrier) === null || _b === void 0 ? void 0 : _b._id) || accessorial.carrier;
    const carrierMismatch = USER_TYPES.CARRIER === type &&
        accessorial.payee_type === 'carrier' &&
        shipmentCarrier !== carrierAssociatedWithAccessorial;
    const isAlreadyApproved = type === USER_TYPES.CARRIER
        ? accessorial.carrier_status === 'approved'
        : accessorial.shipper_status === 'approved';
    const isAlreadyDeclined = type === USER_TYPES.CARRIER
        ? accessorial.carrier_status === 'declined'
        : accessorial.shipper_status === 'declined';
    const disablePayeeApprove = (carrierMismatch && USER_TYPES.CARRIER === type) || isAlreadyApproved;
    const disablePayeeDecline = (carrierMismatch &&
        USER_TYPES.CARRIER === type &&
        accessorialPaid(shipment, accessorial)) ||
        isAlreadyDeclined;
    const disablePayeeEdit = carrierMismatch && USER_TYPES.CARRIER === type;
    const processCheck = type === USER_TYPES.SHIPPER ? accessorialCanBeInvoiced : accessorialPaid;
    const isProcessed = processCheck(shipment, accessorial);
    const userCanApprove = userCanApproveAccessorial(accessorial, type);
    const isMissingDocs = checkMissingDocsByUserType(accessorial, type);
    const isMissingEquipment = checkIfEquipmentIsMissingForAccessorial(accessorial);
    const declinedAccessorial = checkWhichSideAccessorialAutoDeclined(accessorial, type);
    const isCarrierMissing = type === 'carrier' ? isCarrierMissingInAccessorial(accessorial) : false;
    const accessorialApprovalAllowed = () => {
        if (userCanApprove && isAccessorialsAdmin) {
            return true;
        }
        return userCanApprove && !isMissingDocs;
    };
    const renderEditButton = (disabled = false) => (React.createElement(EditButton, { dataTestId: "EditButton-ed69f1e2", disabled: disabled, onClick: () => onEdit(type) }));
    const renderAcceptButton = (disabled = false) => (React.createElement(ApproveButton, { dataTestId: "ApproveButton-c6f695d8", disabled: disabled, onClick: () => onApprove(type) }));
    const renderDeclineButton = (disabled = false) => (React.createElement(DeclineButton, { dataTestId: "DeclineButton-09f71576", disabled: disabled, onClick: () => onDecline(type) }));
    if (isMissingEquipment || declinedAccessorial || isCarrierMissing) {
        return (React.createElement("div", { "data-testid": "div-7704ff1512", className: "flex justify-end -mx-1" },
            React.createElement(OverlayTrigger, { placement: "top", overlay: getToolTip('edit', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                React.createElement("div", { "data-testid": "div-4381acd111", className: "px-1" }, renderEditButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeEdit ||
                    !accessorialFetched ||
                    !isEditable ||
                    isVendorPaid ||
                    declinedAccessorial ||
                    isCarrierMissing ||
                    isAnySaving))),
            React.createElement(OverlayTrigger, { placement: "top", overlay: getToolTip('approve', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                React.createElement("div", { "data-testid": "div-1ee7bce911", className: "px-1" }, renderAcceptButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeApprove ||
                    !accessorialFetched ||
                    !userCanApprove ||
                    isVendorPaid ||
                    isMissingEquipment ||
                    declinedAccessorial ||
                    isCarrierMissing ||
                    isAnySaving))),
            React.createElement(OverlayTrigger, { placement: "top", overlay: getToolTip('declined', isMissingEquipment, declinedAccessorial, isCarrierMissing) },
                React.createElement("div", { "data-testid": "div-4ac0419fsw2", className: "px-1" }, renderDeclineButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeDecline ||
                    isVendorPaid ||
                    declinedAccessorial ||
                    isCarrierMissing ||
                    isAnySaving)))));
    }
    if (isProcessed) {
        return (React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.PROCESSED },
            React.createElement("div", { "data-testid": "div-cc89bbd8", className: "flex justify-end -mx-1" },
                React.createElement("div", { "data-testid": "div-1f9e2468", className: "px-1" }, renderEditButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeEdit ||
                    !isEditable ||
                    isVendorPaid ||
                    isEditSaving)),
                React.createElement("div", { "data-testid": "div-bd11f4e4", className: "px-1" }, renderAcceptButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeApprove ||
                    !userCanApprove ||
                    isMissingDocs ||
                    isVendorPaid ||
                    isEditSaving)),
                React.createElement("div", { "data-testid": "div-2df24545", className: "px-1" }, renderDeclineButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeDecline ||
                    isVendorPaid ||
                    isEditSaving)))));
    }
    if (isMissingDocs) {
        return (React.createElement("div", { "data-testid": "div-7704ff15", className: "flex justify-end -mx-1" },
            React.createElement("div", { "data-testid": "div-4381acd1", className: "px-1" }, renderEditButton(isShipmentCanceled(shipment) ||
                isProcessed ||
                disablePayeeEdit ||
                !accessorialFetched ||
                !isEditable ||
                isVendorPaid ||
                isAnySaving)),
            React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.MISSING_DOCS },
                React.createElement("div", { "data-testid": "div-1ee7bce9", className: "px-1" }, renderAcceptButton(isShipmentCanceled(shipment) ||
                    isProcessed ||
                    disablePayeeApprove ||
                    !accessorialApprovalAllowed() ||
                    isAnySaving))),
            React.createElement("div", { "data-testid": "div-4ac0419f", className: "px-1" }, renderDeclineButton(isShipmentCanceled(shipment) ||
                isProcessed ||
                disablePayeeDecline ||
                isVendorPaid ||
                isAnySaving))));
    }
    return (React.createElement("div", { "data-testid": "div-e7459dea", className: "flex justify-end -mx-1" },
        React.createElement("div", { "data-testid": "div-ede77ccd", className: "px-1" }, renderEditButton(isShipmentCanceled(shipment) ||
            isProcessed ||
            disablePayeeEdit ||
            !accessorialFetched ||
            !isEditable ||
            isVendorPaid ||
            isAnySaving)),
        React.createElement("div", { "data-testid": "div-9afb95e9", className: "px-1" }, renderAcceptButton(isShipmentCanceled(shipment) ||
            isProcessed ||
            disablePayeeApprove ||
            !accessorialFetched ||
            !userCanApprove ||
            isMissingDocs ||
            isVendorPaid ||
            isAnySaving)),
        React.createElement("div", { "data-testid": "div-6bea4cb9", className: "px-1" }, renderDeclineButton(isShipmentCanceled(shipment) ||
            isProcessed ||
            disablePayeeDecline ||
            isVendorPaid ||
            isAnySaving))));
};
export default AccessorialActionButtons;
